// Top bar: thumbnail + round + elapsed seconds (red after 45s) + actions.
// Drag is JS-driven (see `useDragRegion`) so the window never activates and
// GeoGuessr's tab keeps focus.

import { clsx } from "clsx";
import { useEffect, useState } from "react";
import { setOpacity } from "../ipc/commands";
import { copyFinalWithToast } from "../actions/copyFinal";
import { GameModeSelector } from "./GameModeSelector";
import { useDragRegion } from "../hooks/useDragRegion";
import { useStore } from "../state/store";
import { ROUND_STALE_SECONDS } from "../config";

export function TopBar() {
  const round = useStore((s) => s.roundNumber);
  const capturedAtMs = useStore((s) => s.capturedAtMs);
  const thumbnail = useStore((s) => s.thumbnailPath);
  const opacity = useStore((s) => s.opacity);
  const setOpacityLocal = useStore((s) => s.setOpacity);

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const elapsedS =
    capturedAtMs == null ? null : Math.max(0, Math.floor((now - capturedAtMs) / 1000));
  const stale = elapsedS != null && elapsedS >= ROUND_STALE_SECONDS;
  const onDragPointerDown = useDragRegion();

  return (
    <div
      data-testid="top-bar"
      onPointerDown={onDragPointerDown}
      className="flex items-center gap-2 px-3 py-2 border-b border-white/5 select-none cursor-default"
    >
      {thumbnail ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={thumbnail}
          alt="capture thumbnail"
          className="w-9 h-9 rounded border border-white/10"
        />
      ) : (
        <div className="w-9 h-9 rounded border border-dashed border-white/10" />
      )}
      <div className="flex flex-col">
        <div className="text-xs text-fg-muted">Round</div>
        <div className="font-semibold tabular-nums">#{round}</div>
      </div>
      <div className="flex flex-col ml-2">
        <div className="text-xs text-fg-muted">Elapsed</div>
        <div
          className={clsx(
            "font-semibold tabular-nums",
            stale ? "text-[color:var(--err)]" : "text-fg",
          )}
        >
          {elapsedS == null ? "—" : `${elapsedS}s`}
        </div>
      </div>
      <div className="flex-1" />
      <GameModeSelector />
      <button
        type="button"
        onClick={() => useStore.getState().setStatsOpen(true)}
        className="text-xs px-2 py-1 rounded bg-white/5 hover:bg-white/10"
        title="Open the per-pipeline accuracy stats panel"
      >
        📊 Stats
      </button>
      <button
        type="button"
        onClick={() => useStore.getState().setSettingsOpen(true)}
        className="text-xs px-2 py-1 rounded bg-white/5 hover:bg-white/10"
        title="Monitoring + pipeline-disable settings"
      >
        ⚙️ Settings
      </button>
      <button
        type="button"
        onClick={() => void copyFinalWithToast()}
        className="text-xs px-2 py-1 rounded bg-white/5 hover:bg-white/10"
      >
        Copy
      </button>
      <input
        type="range"
        min={0.4}
        max={1}
        step={0.02}
        value={opacity}
        onChange={(e) => {
          const v = Number(e.target.value);
          setOpacityLocal(v);
          void setOpacity(v);
        }}
        className="w-20"
        aria-label="overlay opacity"
      />
    </div>
  );
}
